import { UploadedFile } from '@components/file-field';
import { createForm, useCreateFormStore } from '@components/form';
import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { IconReplace } from '@components/icons/replace';
import { LabelTextGroup } from '@components/label-text-group';
import { usePreviewFileModal } from '@components/modals/preview-file-modal';
import { RadioGroup } from '@components/radio-group';
import { SelectOption } from '@components/select';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import {
  FieldMappingEditorValue,
  FieldMappingVariables,
} from '@components/text-editors/addons/field-mapping';
import { EditorValue } from '@components/text-editors/text-editor';
import { useScopedTranslate } from '@locale/translate';
import { fileNameFromPath } from '@utils/strings';

import { createTravellerJourneyActionFilePath } from 'api/documents/requests';
import { useUploadFile, useUploadedFileUrlFromPath } from 'api/files/hooks';
import { useUpdateJourneyTravellerAction } from 'api/traveller-actions/hooks';
import { MappableActionVariable, TravellerJourneyActionDetailed } from 'api/types';
import { ExecutionMethod } from 'ui/journey-builder/execution-method';
import { DescriptionFormField, useVariableGroups } from 'ui/journey-builder/node-types/common';

import { ACTION_TYPES } from '../action-config';
import {
  ActionProgressBar,
  FormSidebarFooter,
  MappedSource,
  SidebarProps,
  TravellerActionFooter,
  TravellerActionSidebar,
  TriggerMethod,
  isActionError,
  useEditingContext,
} from '../common';
import { getFieldMappingId } from '../helpers';

const ACTION_TYPE = ACTION_TYPES.extract_text;

export const SidebarContent = ({ shortId }: SidebarProps) => (
  <TravellerActionSidebar
    shortId={shortId}
    actionType={ACTION_TYPE}
    Details={DetailsContent}
    Edit={FormContent}
  />
);

type ContentProps = {
  action: TravellerJourneyActionDetailed<typeof ACTION_TYPE>;
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const valuesProperty = action.values?.fileOrUrl ?? 'url';
  const referenceVariables = action.values?.[valuesProperty]?.variables ?? {};
  const firstReferenceVariable = Object.values(referenceVariables)[0];
  const referenceVariablesKeys = Object.keys(referenceVariables);
  const isAttributeReference =
    referenceVariablesKeys.length > 0 &&
    (firstReferenceVariable.source.startsWith('company_traveller_attribute') ||
      firstReferenceVariable.source.startsWith('journey_attribute'));
  const referenceActionId =
    !isAttributeReference && referenceVariablesKeys.length > 0
      ? (referenceVariables[referenceVariablesKeys[0]] as MappableActionVariable).action
      : '';

  const isError = isActionError(action);
  const showMappedSource = isAttributeReference || Boolean(referenceActionId);
  const uploadedFile = action.data.file;

  return (
    <Stack space={6}>
      <ActionProgressBar action={action} isError={isError} isCard={false} />
      <Sidebar.Separator />
      <HideContainer title={t('actionDetails')}>
        <Stack space={4}>
          <LabelTextGroup label={t('description')} text={action.description} />
          <Stack space={1}>
            <Text
              font="semibold14"
              text={valuesProperty === 'url' ? t('fromUrl') : t('fromFile')}
            />
            {showMappedSource ? (
              <MappedSource
                label={firstReferenceVariable ? firstReferenceVariable.label : t('inputFile')}
                isAttribute={isAttributeReference}
                isActionMapped={Boolean(referenceActionId)}
                isUploaded={
                  !isAttributeReference && !referenceActionId && Boolean(action.data.document)
                }
                referenceActionId={referenceActionId}
              />
            ) : action.data.url ? (
              <Text font="regular14" text={action.data.url} />
            ) : uploadedFile ? (
              <UploadedFileFromPath file={uploadedFile} />
            ) : (
              <Text font="regular14" color="elementPurple50" text={t('core.not_set')} />
            )}
          </Stack>
        </Stack>
      </HideContainer>
      {action.savedValues?.output && (
        <>
          <Sidebar.Separator />
          <HideContainer title={t('extractedText')}>
            <Text font="regular14" text={action.savedValues.output} />
          </HideContainer>
        </>
      )}
      <Sidebar.Separator />
      <TravellerActionFooter
        action={action}
        startIcon={<IconReplace />}
        startText={t('extract')}
        retryText={t('retry')}
      />
    </Stack>
  );
};

const UploadedFileFromPath = ({ file }: { file: string | { src: string; name: string } }) => {
  const showFilePreviewModal = usePreviewFileModal();
  const isNotFullPath = typeof file === 'string' && !file.startsWith('http');
  const { data } = useUploadedFileUrlFromPath(isNotFullPath ? file : '');

  const actual =
    typeof file === 'string'
      ? { src: data?.url || file, name: fileNameFromPath(data?.url || file) }
      : {
          src: file.src,
          name: fileNameFromPath(file.name),
        };

  return (
    <UploadedFile
      name={actual.name}
      path={actual.src}
      onPreview={() => {
        showFilePreviewModal({
          formFile: actual,
          filesList: [actual],
        });
      }}
      iconColor="elementPurpleInvert"
      font="regular14"
    />
  );
};

type ExtractTextData = {
  document: EditorValue<{ variables?: FieldMappingVariables }>;
  name: FieldMappingEditorValue;
  description: string;
  isAutomatic: boolean;
  assignees: SelectOption[];
  executionMethod: TriggerMethod;
  file?: FieldMappingEditorValue;
  url?: FieldMappingEditorValue;
  fileOrUrl: 'file' | 'url';
};

const Form = createForm();

const FormContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { stopEditing } = useEditingContext();
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();
  const fieldMappingId = getFieldMappingId(action.journeyId);
  const fileVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
    types: ['file'],
  });
  const urlVariableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journeyId,
    types: ['text'],
  });
  const uploadMutation = useUploadFile(createTravellerJourneyActionFilePath(action), true);

  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });

  const handleSave = async (data: ExtractTextData) => {
    await editMutation.mutateAsync({
      description: data.description,
      isAutomatic: data.executionMethod === 'automatic',
      assignees: data.executionMethod === 'manual' ? data.assignees.map((user) => user.value) : [],
      values: {
        fileOrUrl: data.fileOrUrl,
        file: data.file,
        url: data.url,
      },
    });
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <DescriptionFormField defaultValue={action.description} />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('triggerMethod')}>
          <ExecutionMethod
            assignees={action.assignees}
            isAutomatic={action.isAutomatic}
            hideLabel
            formStore={formStore}
          />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('extractText')}>
          <Form.RadioGroup
            id="fileOrUrl"
            label={t('radio')}
            defaultValue={action.values?.fileOrUrl}
          >
            <RadioGroup.Option label={t('fileOption')} value="file" />
            <RadioGroup.Option label={t('urlOption')} helpText={t('urlOptionHelp')} value="url" />
          </Form.RadioGroup>
          <Form.ConditionalValue id="fileOrUrl" when={(value) => value === 'file'}>
            <Form.FieldMappingFileField
              id="file"
              label={t('file')}
              defaultValue={action.values?.file}
              variableGroups={fileVariableGroups}
              validators={['required']}
              onSelect={uploadMutation.mutateAsync}
              fileType="documentsExtract"
              fieldMappingId={fieldMappingId}
            />
          </Form.ConditionalValue>
          <Form.ConditionalValue id="fileOrUrl" when={(value) => value === 'url'}>
            <Form.FieldMappingTextEditor
              id="url"
              label={t('url')}
              defaultValue={action.values?.url}
              singleLine
              plaintext
              variableGroups={urlVariableGroups}
              validators={['required']}
              fieldMappingId={fieldMappingId}
            />
          </Form.ConditionalValue>
        </HideContainer>
        <FormSidebarFooter Form={Form} isDirty={isDirty} editMutation={editMutation} />
      </Stack>
    </FormWithUnsavedChangesPrompt>
  );
};
const sidebarLocale = {
  en: {
    fileOption: 'File',
    urlOption: 'Online file',
    urlOptionHelp: '(URL)',
    fromUrl: 'Extract from online file (URL)',
    fromFile: 'Extract from file',
    extractText: 'Extract text',
    dataNotFound: 'Data not found',
    description: 'Description',
    radio: 'Extract text from',
    automatic: 'Automatic',
    manual: 'Manual',
    details: 'Details',
    file: 'File',
    url: 'File URL',
    started: 'Started',
    completed: 'Completed',
    retry: 'Re-create',
    inProgress: 'In progress',
    error: 'Error',
    notStarted: 'Not started',
    actionNotRun: 'Action has not been run',
    extract: 'Extract',
    actionDetails: 'Action details',
    triggerMethod: 'Trigger method',
    advanced: 'Advanced',
    extractedText: 'Extracted text',
    inputFile: 'Input file or URL',
    actionHasNotRun: 'Action has not been run',
  },
};
